import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { match } from 'path-to-regexp'
import Tabs from '@mui/material/Tabs'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/styles'
import { useGetHeaderOffset } from 'utils/custom-hooks/useGetHeaderOffset'
import TabLink from './TabLink'

const NavTabs = ({ tabs, variant, orientation, isSideMenu }) => {
  const theme = useTheme()
  const location = useLocation()
  const { showSideMenuText, sideBarWidth } = useSelector(state => state.ui)

  const currentTab = tabs.find(tab => {
    const matcher = match(tab.matchPath)
    return matcher(location.pathname)
  })

  const { headerOffset } = useGetHeaderOffset({ isSideMenu })

  return (
    <Box
      sx={{
        overflow: isSideMenu ? 'auto' : 'unset',
        maxHeight: isSideMenu ? `calc(100vh - ${headerOffset}px)` : 'unset',
        borderColor: 'divider',
        position: isSideMenu ? 'sticky' : 'unset',
        top: isSideMenu ? 0 : 'unset',
        maxWidth: `calc(100vw - ${sideBarWidth}px)`,
        paddingBottom: isSideMenu ? '20px' : 'unset',
      }}
    >
      {isSideMenu ? (
        <Tabs
          value={currentTab ? currentTab.route : false}
          orientation={orientation}
          variant={variant}
          TabIndicatorProps={{ style: { backgroundColor: 'white' } }}
          sx={{
            '.MuiTabs-fixed': {
              padding: `${theme.spacing(2)} 6px 6px 6px`,
            },
          }}
        >
          {tabs.map(tab => (
            <TabLink
              key={tab.route}
              icon={
                showSideMenuText ? (
                  tab.icon
                ) : (
                  <Tooltip title={tab.title} placement="right">
                    <Box>{tab.icon}</Box>
                  </Tooltip>
                )
              }
              label={showSideMenuText && tab.title}
              value={tab.route}
              sx={{
                textWrap: 'nowrap',
                minWidth: 64,
                minHeight: showSideMenuText ? 72 : 60,
                borderRadius: '4px',
                transition: 'background 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.primary.lightBg,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  boxShadow: 3,
                },
              }}
            />
          ))}
        </Tabs>
      ) : (
        <Tabs value={currentTab ? currentTab.route : false} orientation={orientation} variant={variant}>
          {tabs.map(tab => (
            <TabLink
              key={tab.route}
              label={tab.title}
              value={tab.route}
              sx={{
                minWidth: 64,
                minHeight: 72,
                transition: 'opacity 0.3s ease-in-out',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            />
          ))}
        </Tabs>
      )}
    </Box>
  )
}

NavTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      title: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired,
      matchPath: PropTypes.string.isRequired,
    }),
  ).isRequired,
  variant: PropTypes.oneOf(['fullWidth', 'default', 'scrollable']),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  isSideMenu: PropTypes.bool,
}

NavTabs.defaultProps = {
  variant: 'fullWidth',
  orientation: 'horizontal',
  isSideMenu: false,
}

export default NavTabs
